import React, { useState, useRef, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../../contexts/AuthContext';
import KloChatService from '../../services/KloChatService';
import ChatToolResult from './ChatToolResult';

// Simple markdown parser for chat messages (bold, line breaks)
const formatMessage = (text) => {
  if (!text) return null;

  // Split by line breaks first
  const lines = text.split('\n');

  return lines.map((line, lineIdx) => {
    // Parse **bold** within each line
    const parts = [];
    let remaining = line;
    let key = 0;

    while (remaining) {
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
      if (boldMatch) {
        const beforeBold = remaining.substring(0, boldMatch.index);
        if (beforeBold) {
          parts.push(<span key={key++}>{beforeBold}</span>);
        }
        parts.push(<strong key={key++} className="font-semibold">{boldMatch[1]}</strong>);
        remaining = remaining.substring(boldMatch.index + boldMatch[0].length);
      } else {
        if (remaining) {
          parts.push(<span key={key++}>{remaining}</span>);
        }
        break;
      }
    }

    return (
      <span key={lineIdx}>
        {parts.length > 0 ? parts : line}
        {lineIdx < lines.length - 1 && <br />}
      </span>
    );
  });
};

const ChatInputBar = ({ onFocus, isFocusMode }) => {
  const { currentUser } = useAuth();
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);

  const hasMessages = messages.length > 0;
  const showOverlay = isFocused || hasMessages;

  // Dismiss chat - clear messages and reset state
  const dismissChat = () => {
    setMessages([]);
    setConversationId(null);
    setIsFocused(false);
    inputRef.current?.blur();
  };

  // Check if the last assistant message is asking for YES/NO confirmation
  // Only for true confirmations, NOT for open-ended questions
  const lastMessage = messages[messages.length - 1];
  const hasToolResultWithAction = lastMessage?.toolResults?.some(r =>
    // These tool results have their own confirm UI, don't show extra buttons
    r.type === 'client_search' ||
    r.type === 'invoice_created' ||
    r.type === 'email_preview' ||
    r.type === 'invoices_list' ||  // List views are for browsing, not confirming
    r.type === 'clients_list' ||
    r.type === 'bulk_delete_invoices' ||
    r.type === 'bulk_delete_clients' ||
    r.type === 'bulk_mark_paid' ||
    r.type === 'bulk_send_invoices' ||
    r.type === 'delete_invoice' ||
    r.type === 'delete_client'
  );
  const msgLower = lastMessage?.content?.toLowerCase() || '';
  const isAskingConfirmation = lastMessage?.role === 'assistant' &&
    !isLoading &&
    !hasToolResultWithAction &&
    // Strict confirmation patterns - only true yes/no questions
    (msgLower.includes('is that correct') ||
     msgLower.includes('is this correct') ||
     msgLower.includes('does that look right') ||
     msgLower.includes('should i proceed') ||
     msgLower.includes('should i create') ||
     msgLower.includes('should i send') ||
     msgLower.includes('ready to send') ||
     msgLower.includes('want me to send'));

  const suggestions = [
    'Create an invoice',
    'Add a new client',
    'Draft a contract',
    'Schedule a meeting',
  ];

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Global Command-K listener to focus the input
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  const handleSend = useCallback(async () => {
    if (!message.trim() || isLoading) return;

    const userMessage = message.trim();
    setMessage('');
    setIsLoading(true);

    // Add user message and placeholder assistant message together
    setMessages(prev => [
      ...prev,
      { role: 'user', content: userMessage },
      { role: 'assistant', content: '', toolResults: [], isStreaming: true }
    ]);

    try {
      if (!currentUser?.uid) {
        throw new Error('Please sign in to use the assistant');
      }

      await KloChatService.sendMessageStream(
        userMessage,
        currentUser.uid,
        conversationId,
        {
          onConversationId: (id) => {
            setConversationId(id);
          },
          onDelta: (delta) => {
            // Use flushSync to force immediate re-render for each delta
            flushSync(() => {
              setMessages(prev => {
                if (prev.length === 0) return prev;
                const lastMsg = prev[prev.length - 1];
                if (lastMsg && lastMsg.role === 'assistant') {
                  return [
                    ...prev.slice(0, -1),
                    { ...lastMsg, content: (lastMsg.content || '') + delta }
                  ];
                }
                return prev;
              });
            });
          },
          onToolResult: (result) => {
            setMessages(prev => {
              if (prev.length === 0) return prev;
              const lastMsg = prev[prev.length - 1];
              if (lastMsg && lastMsg.role === 'assistant') {
                return [
                  ...prev.slice(0, -1),
                  { ...lastMsg, toolResults: [...(lastMsg.toolResults || []), result] }
                ];
              }
              return prev;
            });
          },
          onDone: () => {
            setMessages(prev => {
              if (prev.length === 0) return prev;
              const lastMsg = prev[prev.length - 1];
              return [
                ...prev.slice(0, -1),
                { ...lastMsg, isStreaming: false }
              ];
            });
            setIsLoading(false);
          },
          onError: (error) => {
            console.error('Chat error:', error);
            setMessages(prev => {
              if (prev.length === 0) return prev;
              const lastMsg = prev[prev.length - 1];
              if (lastMsg && lastMsg.role === 'assistant') {
                return [
                  ...prev.slice(0, -1),
                  { ...lastMsg, content: "I had trouble with that request. Could you try again?", isStreaming: false }
                ];
              }
              return prev;
            });
            setIsLoading(false);
          }
        }
      );
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => {
        if (prev.length === 0) return prev;
        const lastMsg = prev[prev.length - 1];
        if (lastMsg && lastMsg.role === 'assistant') {
          return [
            ...prev.slice(0, -1),
            { ...lastMsg, content: "I had trouble with that request. Could you try again?", isStreaming: false }
          ];
        }
        return prev;
      });
      setIsLoading(false);
    }
  }, [message, isLoading, currentUser, conversationId, messages.length]);

  const handleKeyDown = (e) => {
    // ⌘+Enter to confirm when confirmation buttons are showing
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && isAskingConfirmation) {
      e.preventDefault();
      handleToolAction('confirm');
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    if (e.key === 'Escape') {
      setIsFocused(false);
      setMessages([]);
      setConversationId(null);
      inputRef.current?.blur();
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = (e) => {
    // Don't blur if clicking within the chat area
    if (e.relatedTarget?.closest('.chat-container')) return;
    if (!hasMessages) {
      setIsFocused(false);
    }
  };

  const closeChat = async () => {
    // Clear conversation on backend if we have one
    if (conversationId && currentUser?.uid) {
      try {
        await KloChatService.clearConversation(conversationId, currentUser.uid);
      } catch (e) {
        // Ignore errors on clear
      }
    }
    setIsFocused(false);
    setMessages([]);
    setMessage('');
    setConversationId(null);
  };

  // Handle tool result actions (like clicking a client to select)
  const handleToolAction = useCallback(async (action, data) => {
    if (isLoading) return;

    let responseMessage = '';
    let displayMessage = '';

    if (action === 'select_client' && data?.client) {
      // Clear, direct confirmation for client selection
      responseMessage = `Yes, use ${data.client.name}`;
      displayMessage = responseMessage;
    } else if (action === 'create_client' && data?.name && data?.email) {
      // Create new client - use direct command format
      responseMessage = `create client ${data.name} ${data.email}`;
      displayMessage = `Add ${data.name}`;
    } else if (action === 'confirm') {
      responseMessage = 'Yes';
      displayMessage = 'Yes';
    } else if (action === 'send_email') {
      // If we have invoiceId, send directly without going through AI
      if (data?.invoiceId) {
        responseMessage = `send invoice ${data.invoiceId}`;
      } else {
        responseMessage = 'Yes, send it';
      }
      displayMessage = 'Send it';
    } else if (action === 'confirm_delete_invoice' && data?.id) {
      // Confirm invoice deletion
      responseMessage = `confirm delete invoice ${data.id}`;
      displayMessage = 'Delete it';
    } else if (action === 'confirm_delete_client' && data?.id) {
      // Confirm client deletion
      responseMessage = `confirm delete client ${data.id}`;
      displayMessage = 'Delete';
    } else if (action === 'cancel_delete') {
      // Cancel deletion
      responseMessage = 'No, keep it';
      displayMessage = 'Keep it';
    } else if (action === 'confirm_bulk_delete_invoices' && data?.invoiceIds) {
      // Confirm bulk delete invoices
      responseMessage = `confirm bulk delete invoices ${data.invoiceIds.join(',')}`;
      displayMessage = 'Delete all';
    } else if (action === 'confirm_bulk_delete_clients' && data?.clientIds) {
      // Confirm bulk delete clients
      responseMessage = `confirm bulk delete clients ${data.clientIds.join(',')}`;
      displayMessage = 'Delete all';
    } else if (action === 'confirm_bulk_mark_paid' && data?.invoiceIds) {
      // Confirm bulk mark as paid
      responseMessage = `confirm bulk mark paid ${data.invoiceIds.join(',')}`;
      displayMessage = 'Mark all paid';
    } else if (action === 'confirm_bulk_send_invoices' && data?.invoiceIds) {
      // Confirm bulk send invoices
      responseMessage = `confirm bulk send invoices ${data.invoiceIds.join(',')}`;
      displayMessage = 'Send all';
    } else if (action === 'cancel_bulk_action') {
      // Cancel bulk action
      responseMessage = 'No, cancel';
      displayMessage = 'Cancel';
    }

    if (responseMessage) {
      setIsLoading(true);

      // Add user message and placeholder assistant message together
      setMessages(prev => [
        ...prev,
        { role: 'user', content: displayMessage },
        { role: 'assistant', content: '', toolResults: [], isStreaming: true }
      ]);

      try {
        await KloChatService.sendMessageStream(
          responseMessage,
          currentUser.uid,
          conversationId,
          {
            onConversationId: (id) => {
              setConversationId(id);
            },
            onDelta: (delta) => {
              // Use flushSync to force immediate re-render for each delta
              flushSync(() => {
                setMessages(prev => {
                  if (prev.length === 0) return prev;
                  const lastMsg = prev[prev.length - 1];
                  if (lastMsg && lastMsg.role === 'assistant') {
                    return [
                      ...prev.slice(0, -1),
                      { ...lastMsg, content: (lastMsg.content || '') + delta }
                    ];
                  }
                  return prev;
                });
              });
            },
            onToolResult: (result) => {
              setMessages(prev => {
                if (prev.length === 0) return prev;
                const lastMsg = prev[prev.length - 1];
                if (lastMsg && lastMsg.role === 'assistant') {
                  return [
                    ...prev.slice(0, -1),
                    { ...lastMsg, toolResults: [...(lastMsg.toolResults || []), result] }
                  ];
                }
                return prev;
              });
            },
            onDone: () => {
              setMessages(prev => {
                if (prev.length === 0) return prev;
                const lastMsg = prev[prev.length - 1];
                return [
                  ...prev.slice(0, -1),
                  { ...lastMsg, isStreaming: false }
                ];
              });
              setIsLoading(false);
            },
            onError: (error) => {
              console.error('Chat error:', error);
              setMessages(prev => {
                if (prev.length === 0) return prev;
                const lastMsg = prev[prev.length - 1];
                if (lastMsg && lastMsg.role === 'assistant') {
                  return [
                    ...prev.slice(0, -1),
                    { ...lastMsg, content: "I had trouble with that request. Could you try again?", isStreaming: false }
                  ];
                }
                return prev;
              });
              setIsLoading(false);
            }
          }
        );
      } catch (error) {
        console.error('Chat error:', error);
        setMessages(prev => {
          if (prev.length === 0) return prev;
          const lastMsg = prev[prev.length - 1];
          if (lastMsg && lastMsg.role === 'assistant') {
            return [
              ...prev.slice(0, -1),
              { ...lastMsg, content: "I had trouble with that request. Could you try again?", isStreaming: false }
            ];
          }
          return prev;
        });
        setIsLoading(false);
      }
    }
  }, [isLoading, currentUser, conversationId]);

  // Global keyboard listener for ⌘+Enter confirmation
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && isAskingConfirmation && !isLoading) {
        e.preventDefault();
        handleToolAction('confirm');
      }
    };

    if (isAskingConfirmation) {
      window.addEventListener('keydown', handleGlobalKeyDown);
      return () => window.removeEventListener('keydown', handleGlobalKeyDown);
    }
  }, [isAskingConfirmation, isLoading, handleToolAction]);

  return (
    <>
      {/* Gradient blur overlay - fades from bottom to top, clickable to dismiss when messages exist */}
      <AnimatePresence>
        {showOverlay && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={`absolute inset-0 z-10 ${hasMessages ? 'cursor-pointer' : 'pointer-events-none'} dark:hidden`}
            onClick={hasMessages ? dismissChat : undefined}
            style={{
              background: hasMessages
                ? 'linear-gradient(to top, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.95) 30%, rgba(255,255,255,0.7) 60%, rgba(255,255,255,0.3) 80%, transparent 100%)'
                : 'linear-gradient(to top, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.6) 20%, rgba(255,255,255,0.3) 40%, transparent 60%)',
            }}
          />
        )}
      </AnimatePresence>
      {/* Dark mode overlay */}
      <AnimatePresence>
        {showOverlay && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={`absolute inset-0 z-10 ${hasMessages ? 'cursor-pointer' : 'pointer-events-none'} hidden dark:block`}
            onClick={hasMessages ? dismissChat : undefined}
            style={{
              background: hasMessages
                ? 'linear-gradient(to top, rgba(17,17,17,0.98) 0%, rgba(17,17,17,0.95) 30%, rgba(17,17,17,0.7) 60%, rgba(17,17,17,0.3) 80%, transparent 100%)'
                : 'linear-gradient(to top, rgba(17,17,17,0.9) 0%, rgba(17,17,17,0.6) 20%, rgba(17,17,17,0.3) 40%, transparent 60%)',
            }}
          />
        )}
      </AnimatePresence>

      {/* Chat container - positioned at bottom */}
      <div className="relative z-20 flex-shrink-0 chat-container">
        <div className="pl-4 pr-20 lg:px-6 pb-4">
          <div className="max-w-2xl mx-auto lg:mx-auto">

            {/* Messages area - expands when there are messages */}
            <AnimatePresence>
              {hasMessages && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mb-4 max-h-[60vh] overflow-auto"
                >
                  <div className="space-y-3 py-2">
                    {messages.map((msg, index) => {
                      // Don't render empty streaming assistant messages (loading dots show instead)
                      const isEmptyStreaming = msg.role === 'assistant' && msg.isStreaming && !msg.content && (!msg.toolResults?.length);
                      if (isEmptyStreaming) return null;

                      return (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}
                        >
                          {/* Message bubble - user messages have bubble, assistant messages don't */}
                          {(msg.role === 'user' || msg.content) && (
                            <div
                              className={`max-w-[85%] text-sm leading-relaxed ${
                                msg.role === 'user'
                                  ? 'px-4 py-3 rounded-2xl bg-gray-900 dark:bg-brand text-white'
                                  : 'text-gray-900 dark:text-gray-100'
                              }`}
                            >
                              {msg.role === 'assistant' ? formatMessage(msg.content) : msg.content}
                            </div>
                          )}

                          {/* Tool results (only for assistant messages) */}
                          {msg.role === 'assistant' && msg.toolResults && msg.toolResults.length > 0 && (
                            <div className={`${msg.content ? 'mt-2' : ''} w-full max-w-[85%] space-y-3`}>
                              {msg.toolResults.map((result, idx) => {
                                // Only the last tool result in the last message gets keyboard shortcuts
                                const isLastMessage = index === messages.length - 1;
                                const isLastResult = idx === msg.toolResults.length - 1;
                                const isLatest = isLastMessage && isLastResult;
                                return (
                                  <ChatToolResult
                                    key={idx}
                                    result={result}
                                    onAction={handleToolAction}
                                    isLatest={isLatest}
                                  />
                                );
                              })}
                            </div>
                          )}
                        </motion.div>
                      );
                    })}

                    {/* Single blinking dot - only show when waiting for first content */}
                    {(() => {
                      const lastMsg = messages[messages.length - 1];
                      const showWaitingDot = lastMsg?.role === 'assistant' && lastMsg?.isStreaming && !lastMsg?.content && (!lastMsg?.toolResults?.length);
                      return showWaitingDot ? (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="flex items-start"
                        >
                          <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-pulse" />
                        </motion.div>
                      ) : null;
                    })()}

                    <div ref={messagesEndRef} />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Suggestions - appear above input when focused, no messages */}
            <AnimatePresence>
              {isFocused && !hasMessages && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                  className="flex flex-wrap justify-center gap-2 mb-3"
                >
                  {suggestions.map((suggestion, i) => (
                    <motion.button
                      key={i}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                      whileHover={{ y: -1 }}
                      onClick={() => {
                        setMessage(suggestion);
                        inputRef.current?.focus();
                      }}
                      className="px-3.5 py-2 text-sm text-gray-500 dark:text-gray-400 bg-white/80 dark:bg-[#1A1A1A]/80 backdrop-blur-sm rounded-full border border-gray-200 dark:border-[#333] hover:bg-white dark:hover:bg-[#252525] hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-[#444] transition-all shadow-sm"
                    >
                      {suggestion}
                    </motion.button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Quick reply buttons - appear when AI asks a yes/no question */}
            <AnimatePresence>
              {isAskingConfirmation && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.15 }}
                  className="flex items-center justify-end gap-2 mb-3"
                >
                  <span className="text-xs text-gray-400 dark:text-gray-500 mr-1">
                    {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Enter
                  </span>
                  <button
                    onClick={() => handleToolAction('confirm')}
                    className="px-4 py-2 bg-brand text-white rounded-xl hover:bg-brand-hover transition-colors text-sm font-medium shadow-sm"
                  >
                    Yes
                  </button>
                  <button
                    onClick={() => {
                      setMessages(prev => [...prev, { role: 'user', content: 'No, let me make changes' }]);
                    }}
                    className="px-4 py-2 bg-gray-100 dark:bg-[#2A2A2A] text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-[#333] transition-colors text-sm font-medium"
                  >
                    No
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Input bar */}
            <motion.div
              animate={{
                y: showOverlay ? 0 : 0,
              }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              className="flex items-center gap-2 bg-white dark:bg-[#1A1A1A] rounded-full px-4 py-2 transition-all duration-300 border border-transparent dark:border-[#333]"
              style={{
                boxShadow: isHovered || isFocused
                  ? '0 8px 32px rgba(249, 115, 22, 0.25), 0 4px 16px rgba(249, 115, 22, 0.15)'
                  : '0 4px 20px rgba(249, 115, 22, 0.12), 0 2px 8px rgba(249, 115, 22, 0.08)'
              }}
            >
              {/* Keyboard shortcut hint - show when not focused and no messages */}
              {!isFocused && !hasMessages && (
                <div className="flex items-center gap-2 text-gray-400 dark:text-gray-500 text-sm pl-1">
                  <span className="text-gray-500 dark:text-gray-400">Ask Klo</span>
                  <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-[#2A2A2A] rounded text-[10px] font-mono">
                    {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}K
                  </kbd>
                </div>
              )}

              <input
                ref={inputRef}
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder={isFocused || hasMessages ? "Ask Klo..." : ""}
                disabled={isLoading}
                className="flex-1 px-2 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-transparent outline-none focus:outline-none focus:ring-0 border-none disabled:opacity-50"
              />

              {/* Close button when chat is active */}
              {hasMessages && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  onClick={closeChat}
                  className="w-9 h-9 flex items-center justify-center rounded-full text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-all"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </motion.button>
              )}

              <button
                onClick={handleSend}
                disabled={!message.trim() || isLoading}
                className={`w-9 h-9 flex items-center justify-center rounded-full transition-all ${
                  message.trim() && !isLoading
                    ? 'bg-brand text-white hover:bg-brand-hover'
                    : 'bg-gray-100 dark:bg-[#2A2A2A] text-gray-300 dark:text-gray-600 cursor-not-allowed'
                }`}
              >
                <ArrowUpIcon className="w-4 h-4" />
              </button>
            </motion.div>

            {/* Hint when focused */}
            <AnimatePresence>
              {isFocused && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="mt-2 text-center text-[11px] text-gray-400 dark:text-gray-500"
                >
                  <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-[#2A2A2A] rounded text-[10px] font-mono">esc</kbd>
                  <span className="ml-1.5">to close</span>
                </motion.p>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatInputBar;
